import { api } from '../../shared/api/client';
import { adaptRequest } from '../../shared/api/adapters';
import type { ApiRequest, PaginatedResponse } from '../../shared/types/api';
import type { RequestItem, BookingDraft } from '../../shared/types/app';

function buildQuery(params: Record<string, string | number | boolean | undefined>) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q.set(k, String(v));
  }
  return q.toString() ? `?${q.toString()}` : '';
}

export async function fetchRequests(params?: {
  status?: string;
  driverId?: string;
  page?: number;
  limit?: number;
}) {
  const qs = buildQuery({
    status: params?.status,
    driverId: params?.driverId,
    page: params?.page,
    limit: params?.limit,
  });
  const res = await api.get<PaginatedResponse<ApiRequest> | ApiRequest[]>(`/api/v1/requests${qs}`);
  if (Array.isArray(res)) {
    return { data: res.map(adaptRequest), total: res.length, page: 1, limit: res.length, totalPages: 1 };
  }
  return { ...res, data: res.data.map(adaptRequest) };
}

export async function fetchRequest(id: string): Promise<RequestItem> {
  const res = await api.get<ApiRequest>(`/api/v1/requests/${id}`);
  return adaptRequest(res);
}

// TimePicker's onChange (see shared/ui/TimePicker.tsx#to24h) emits 24h "HH:MM", not "H:MM AM/PM"
// — matching against the wrong format here silently fell through to midnight every time,
// regardless of what the customer actually picked.
function toIsoDateTime(date: string, time?: string | null): string {
  if (time) {
    const m = time.match(/^(\d{1,2}):(\d{2})$/);
    if (m) {
      const hh = m[1].padStart(2, '0');
      const mm = m[2];
      return new Date(`${date}T${hh}:${mm}:00`).toISOString();
    }
  }
  return new Date(`${date}T00:00:00`).toISOString();
}

export async function createRequest(draft: BookingDraft): Promise<RequestItem> {
  const res = await api.post<ApiRequest>('/api/v1/requests', {
    pickupLat: draft.pickupLat ?? 0,
    pickupLng: draft.pickupLng ?? 0,
    pickupAddress: draft.pickup,
    dropoffLat: draft.dropoffLat ?? 0,
    dropoffLng: draft.dropoffLng ?? 0,
    dropoffAddress: draft.dropoff,
    equipmentType: draft.equipmentKind,
    equipmentNotes: draft.equipmentDesc || undefined,
    make: draft.make,
    model: draft.model,
    estimatedWeight: draft.estimatedWeight,
    photoUrls: draft.photoUrls,
    scheduledAt: draft.scheduledDate
      ? toIsoDateTime(draft.scheduledDate, draft.exactTime)
      : undefined,
    isImmediate: !draft.scheduledDate,
    loadingInstructions: draft.notes || undefined,
    weightClass: draft.weightClass,
    distanceKm: draft.distanceKm,
    contactName: draft.contactName,
    contactPhone: draft.contactPhone,
    company: draft.company || undefined,
    deliveryTime: draft.exactTime || undefined,
    slotId: draft.slotId || undefined,
  });
  return adaptRequest(res);
}

export async function cancelRequest(id: string) {
  return api.patch<ApiRequest>(`/api/v1/requests/${id}/cancel`);
}

// Only permitted once a request is Cancelled — see rtBackend requests/service.ts#remove.
export async function deleteRequest(id: string) {
  return api.delete<{ message: string }>(`/api/v1/requests/${id}`);
}

